import * as joda from "@js-joda/core";
import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import {
  dynStyleVariadic,
  dynVariadic,
  scheduleForCleanup,
} from "trader.ts/ui";
import { checkAllCasesHandled } from "trader.ts/utils";
import * as planningstyle from "../../../styles/planning.css.classes";
import { isNil } from "../../lib/istype";
import { planningClientMode } from "./client";
import * as style from "../../../styles/style.css.classes";

export function renderFloatingPopup<Mode, Slot, Details>(
  modeS: Source<Mode>,
  slotsS: Source<Slot[]>,
  smallLayoutS: Source<boolean> | null,
  findSlot: (m: Mode, slots: Slot[]) => Details | null | undefined,
  opts: {
    getSelectedElementDimensions: (details: Details) => {
      x: number;
      y: number;
      height: number;
      width: number;
    };
    calcPopupWidth: (m: Mode) => number;
    getApproximateFloatingPopupHeight: (m: Mode) => number;
    renderPopupContents: (
      details: Details,
      slots: Slot[],
    ) => HTMLElement | HTMLElement[];
    modeIsDayView: (m: Mode) => boolean;
    offsetLeft: number; // pixels to move popup when displaying popup on left side
    offsetRight: number; // pixels to move popup when displaying popup on right side
  },
) {
  const floatingPopupHeightS = new Source(0);
  window.addEventListener("resize", () =>
    floatingPopupHeightS.set(floatingPopupHeightS.get()),
  );
  const obs = new ResizeObserver(() => {
    const h = popupContents.getBoundingClientRect().height;
    const approx = opts.getApproximateFloatingPopupHeight(modeS.get());
    const setting = Math.max(h, approx);
    if (setting > 0) {
      floatingPopupHeightS.set(setting);
    }
  });

  // Have to wait for calendar to render initially
  const firstRenderDoneS = new Source(false);
  setTimeout(() => firstRenderDoneS.set(true), 1);
  let prevMode: Mode = modeS.get();
  modeS.observe((mode) => {
    // When we navigate from day to week, we need to wait for the first render again
    // so we can find the rendered slot on the calendar
    if (opts.modeIsDayView(prevMode) && !opts.modeIsDayView(mode)) {
      firstRenderDoneS.set(false);
      setTimeout(() => firstRenderDoneS.set(true), 1);
    }
    prevMode = mode;
  });
  // We position popup fixed and disable scrolling
  // We can't render the popup inside the <main>, as overflows will never show as the <main> scrolls
  const popupContents = (
    <div>
      {dynVariadic(
        [modeS, slotsS, smallLayoutS || new Source(false)],
        ([mode, slots, smallLayout]) => {
          const details = findSlot(mode, slots);
          if (isNil(details)) {
            return <span></span>;
          } else {
            return (
              <div
                className={smallLayout ? style.floating_popup_small_layout : ""}
              >
                {opts.renderPopupContents(details, slots)}
              </div>
            );
          }
        },
      )}
    </div>
  );

  obs.observe(popupContents);
  scheduleForCleanup(() => obs.unobserve(popupContents));

  // We only want to transition to MOVE the popup, not the SHOW it initially
  const transitionStyle = "; transition: all 0.1s ease-in-out;";

  return dynStyleVariadic(
    [
      modeS,
      slotsS,
      firstRenderDoneS,
      floatingPopupHeightS,
      smallLayoutS || new Source(false),
    ],
    ([mode, slots, firstRenderDone, floatingPopupHeight, smallLayout]) => {
      const details = findSlot(mode, slots);
      if (!firstRenderDone || isNil(details)) {
        return "display: none";
      }
      if (opts.modeIsDayView(mode) && !smallLayout) {
        // To align details with daycalendar
        return "margin-top: 40px;";
      }
      if (details) {
        if (smallLayout) {
          return `position: fixed;
                  width: 100vw;
                  left: 0vw;
                  bottom: 0vh;
                  max-height: 95vh;
                  max-height: 95dvh;
                  overflow: auto;
                  z-index: 20;

                  animation: slide_in_from_bot_anim 0.25s;
                  animation-fill-mode: forwards;
                  animation-timing-function: ease-out;`;
        } else {
          return (
            calcFloatingPopupPositionStyle(
              opts.getSelectedElementDimensions(details),
              floatingPopupHeight,
              opts.calcPopupWidth(mode),
              { offsetRight: opts.offsetLeft, offsetLeft: opts.offsetLeft },
            ) + transitionStyle
          );
        }
      } else {
        return "display: none";
      }
    },
    popupContents,
  );
}

export function getApproximateFloatingPopupHeightForPlanning(
  mode: planningClientMode,
) {
  if (mode.tag === "selected_existing_slot") {
    return 360;
  } else if (mode.tag == "selected_new") {
    if (mode.subtype === "private") {
      return 400;
    } else if (mode.subtype === "group" || mode.subtype === "camp") {
      return 400;
    } else if (mode.subtype === "free") {
      return 200;
    } else if (mode.subtype === "absence") {
      return 200;
    } else {
      return checkAllCasesHandled(mode.subtype);
    }
  } else if (
    mode.tag === "editing" ||
    mode.tag === "moving_selected" ||
    mode.tag === "moving_enrollment_selected"
  ) {
    return 480;
  } else if (
    mode.tag === "normal" ||
    mode.tag === "selecting_new" ||
    mode.tag === "moving_enrollment" ||
    mode.tag === "moving"
  ) {
    return 0;
  } else if (mode.tag === "making_complement") {
    return 280;
  } else {
    return checkAllCasesHandled(mode);
  }
}

export function getFloatingPopupWidthForPlanning(mode: planningClientMode) {
  if (mode.tag === "selected_existing_slot") {
    return 480;
  } else if (
    mode.tag == "selected_new" ||
    mode.tag == "making_complement" ||
    mode.tag === "moving_selected" ||
    mode.tag === "moving_enrollment_selected"
  ) {
    return 480;
  } else if (mode.tag === "editing") {
    return 480;
  } else if (
    mode.tag === "normal" ||
    mode.tag === "selecting_new" ||
    mode.tag === "moving_enrollment" ||
    mode.tag === "moving"
  ) {
    return 0;
  } else {
    return checkAllCasesHandled(mode);
  }
}

function calcFloatingPopupPositionStyle(
  rect: {
    x: number;
    y: number;
    height: number;
    width: number;
  },
  approximateHeight: number,
  width: number,
  opts: {
    offsetRight: number;
    offsetLeft: number;
  },
) {
  const windowMid = window.innerHeight / 2;

  const topIfPerfectCenter = windowMid - approximateHeight / 2;
  const botIfPerfectCenter = topIfPerfectCenter + approximateHeight;

  // Calc base = we take the average of the slot's top and the popup's middle
  // but the closer we get to the edges, the more we count the slot's position
  // So all the way up we are flush with the slot's top and
  // in the middle the popup should be very close to the middle of the screen

  const fractionOfSlot_t = 1 - rect.y / (window.innerHeight / 2);
  const fractionOfMidPopup_t = 1 - fractionOfSlot_t;

  const fractionOfSlot_b =
    1 -
    Math.abs(window.innerHeight - (rect.y + rect.height)) /
      (window.innerHeight / 2);
  const fractionOfMidPopup_b = 1 - fractionOfSlot_b;

  // If the popup is bigger than the screen -> just lock it to the top/bottom
  // height = 99vh will take care of the scrolling
  const lowestPossibleTopToFitEverything =
    window.innerHeight * 0.85 <= approximateHeight ? 4 : null;

  const highestPossibleBottomToFitEverything =
    window.innerHeight * 0.85 <= approximateHeight ? 4 : null;

  const yPos =
    rect.y < windowMid
      ? {
          top:
            lowestPossibleTopToFitEverything ||
            Math.max(
              2, // Don't ever go minus height
              Math.min(
                // Put popup "close to" selected slot
                topIfPerfectCenter * fractionOfMidPopup_t +
                  rect.y * fractionOfSlot_t,
                rect.y, // Always be at least above selected slot
              ),
            ),
        }
      : {
          bottom:
            highestPossibleBottomToFitEverything ||
            Math.max(
              2,
              Math.min(
                window.innerHeight -
                  (botIfPerfectCenter * fractionOfMidPopup_b +
                    (rect.y + rect.height) * fractionOfSlot_b),
                window.innerHeight - (rect.y + rect.height),
              ),
            ),
        };

  const xPos =
    rect.x + rect.width + opts.offsetRight + width < window.innerWidth
      ? { left: rect.x + rect.width + opts.offsetRight }
      : {
          right: window.innerWidth - rect.x + opts.offsetLeft,
        };

  const positionStyle =
    `
      position: fixed;
      width: ${width}px;
      z-index: 1000;
    ` +
    ("left" in xPos ? `left: ${xPos.left}px` : `right: ${xPos.right}px`) +
    ";\n" +
    ("top" in yPos ? `top: ${yPos.top}px` : `bottom: ${yPos.bottom}px`) +
    ";\n";

  return positionStyle;
}

// On the "calendar" based screens
// we don't grab the actual HTML element to do the calculations,
// but rather grab the calendar element and get weekday/time/duration and calculate from there
//
// I'm not 100% sure why I did it like this in the first place,
// but it does make it nice and stable, and it doesn't require assigning some unique id scheme
// to all rendered slots
export function calcSelectedElementDimensionsForCalendarSlot(
  startHour: number,
  selectedSlot: {
    weekday: joda.DayOfWeek;
    time: joda.LocalTime;
    duration: number;
  },
): {
  x: number;
  y: number;
  height: number;
  width: number;
} {
  const hourHeight = 80;
  const minuteHeight = hourHeight / 60;
  const table = document.querySelector("." + planningstyle.calendar.trim());
  if (!table) {
    throw new Error("Failed to find calendar");
  }
  // const mainScrollBy = document.querySelector("main")?.scrollTop || 0;

  const row = (table as HTMLTableElement).rows[1];
  const col = row.cells[selectedSlot.weekday.ordinal() + 1];
  const rect = col.getBoundingClientRect();

  const dur = joda.Duration.between(
    joda.LocalTime.of(startHour),
    selectedSlot.time,
  );

  const selectedSlotTop = rect.y + (dur.toMinutes() * hourHeight) / 60;
  const selectedSlotBot =
    selectedSlotTop + selectedSlot.duration * minuteHeight;

  return {
    x: rect.x,
    y: selectedSlotTop,
    width: rect.width,
    height: selectedSlotBot - selectedSlotTop,
  };
}

export function makeAndTrackSmallLayoutS(opts: {
  maxWidth: number;
}): Source<boolean> {
  const smallLayoutS = new Source(false);
  function calcSmallLayoutS() {
    const newVal = window.innerWidth < opts.maxWidth;
    if (newVal !== smallLayoutS.get()) {
      smallLayoutS.set(newVal);
    }
  }
  calcSmallLayoutS();
  window.addEventListener("resize", calcSmallLayoutS);
  scheduleForCleanup(() =>
    window.removeEventListener("resize", calcSmallLayoutS),
  );
  return smallLayoutS;
}
